import {
  Row,
  Col,
  Card,
  Table,
  Button,
  Tag,
  Tooltip,
  Input,
  Select,
} from "antd";
import React, { useState, useEffect } from "react";
import useRequest from "../../hooks/useRequest";
import { ShowToast, Severty } from "../../helper/toast";
import ShowTotal from "../../components/ShowTotal";
import apiPath from "../../constants/apiPath";
import lang from "../../helper/lang";
import useDebounce from "../../hooks/useDebounce";
import { ExclamationCircleOutlined, UndoOutlined } from "@ant-design/icons";
import FilterBar, { FilterBarFilters } from "../../components/FilterBar";
import confirm from "antd/lib/modal/confirm";
import IntegrationForm from "./AddForm";

const { Search } = Input;
const { Option } = Select;

function Index() {
  const { request } = useRequest();

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [visible, setVisible] = useState(false);
  const [searchText, setSearchText] = useState("");
  const debouncedSearchText = useDebounce(searchText, 300);
  const [filters, setFilters] = useState({ status: undefined });
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState(null);
  const [isUpdate, setIsUpdate] = useState(false);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  // ----------- FETCH DATA ---------------
  const fetchData = () => {
    setLoading(true);

    const params = new URLSearchParams();

    if (debouncedSearchText) {
      params.append("search", debouncedSearchText);
    }

    if (filters.status !== undefined) {
      params.append("status", filters.status);
    }

    // 📄 Pagination
    params.append("page", pagination?.current);
    params.append("limit", pagination?.pageSize);

    request({
      url: `${apiPath.listIntegrations}?${params.toString()}`,
      method: "GET",
      onSuccess: ({ data }) => {
        setLoading(false);
        setList(data?.data || []);
        setPagination((prev) => ({
          current: pagination?.current,
          total: data?.total,
          pageSize: Number(data?.limit),
        }));
      },
      onError: (error) => {
        setLoading(false);
        ShowToast(error, Severty.ERROR);
      },
    });
  };

  const resetData = () => {
    setSearchText("");
    setFilters({ status: undefined });
    setRefresh((prev) => !prev);
  };

  // ----------- ENABLE / DISABLE ----------
  const confirmStatusChange = (record) => {
    confirm({
      icon: <ExclamationCircleOutlined />,
      content: (
        <p style={{ fontSize: 18, fontWeight: "bold", textAlign: "center" }}>
          Are you sure you want to {record.isEnabled ? "disable" : "enable"}{" "}
          this integration?
        </p>
      ),
      onOk() {
        request({
          url: `${apiPath.updateIntegration}/${record._id}`,
          method: "PUT",
          data: { isEnabled: !record.isEnabled },
          onSuccess: (res) => {
            ShowToast(res.message, Severty.SUCCESS);
            fetchData();
          },
          onError: (error) => {
            ShowToast(error, Severty.ERROR);
          },
        });
      },
    });
  };

  // ----------- TABLE COLUMNS -------------
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
    },
    {
      title: "Code",
      dataIndex: "code",
    },
    {
      title: "Category",
      dataIndex: "category",
      render: (category) => <Tag color="blue">{category}</Tag>,
    },
    {
      title: "Provider",
      dataIndex: "provider",
    },
    {
      title: "API Key",
      dataIndex: "apiKey",
      render: (apiKey) => <Tag>****{apiKey?.slice(-4)}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "isEnabled",
      render: (isEnabled) => (
        <Tag color={isEnabled ? "green" : "red"}>
          {isEnabled ? "Enabled" : "Disabled"}
        </Tag>
      ),
    },
    {
      title: lang("Action"),
       
      render: (_, record) => (
        <>
          <Tooltip title="Edit Integration">
            <Button
              onClick={() => {
                setSelected(record);
                setIsUpdate(true);
                setVisible(true);
              }}
            >
              <i className="fa fa-pen" />
            </Button>
          </Tooltip>

          <Tooltip title={record.isEnabled ? "Disable" : "Enable"}>
            <Button onClick={() => confirmStatusChange(record)}>
              <i className="fa fa-power-off" />
            </Button>
          </Tooltip>
        </>
      ),
    },
  ];

  // ----------- EFFECT -------------------
  useEffect(() => {
    fetchData();
  }, [filters, refresh, debouncedSearchText]);

  return (
    <>
      <div className="tabled">
        <Row gutter={[24, 0]}>
          <Col span={24}>
            <Card
              bordered={false}
              className="criclebox tablespace mb-24"
              title="API & Integration Manager"
              extra={
                <FilterBar
                  showFilters={showFilters}
                  onToggle={() => setShowFilters((v) => !v)}
                  searchContent={
                    <Search
                      size="large"
                      placeholder="Search by name or code"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      allowClear
                    />
                  }
                  rightContent={
                    <Button
                      type="primary"
                      onClick={() => {
                        setSelected(null);
                        setIsUpdate(false);
                        setVisible(true);
                      }}
                    >
                      Add Integration
                    </Button>
                  }
                />
              }
            >
              {showFilters && (
                <FilterBarFilters>
                  <Select
                    placeholder="Status"
                    allowClear
                    value={filters.status}
                    onChange={(value) =>
                      setFilters({ ...filters, status: value })
                    }
                  >
                    <Option value={true}>Enabled</Option>
                    <Option value={false}>Disabled</Option>
                  </Select>
                  <Button icon={<UndoOutlined />} onClick={resetData}>
                    Reset
                  </Button>
                </FilterBarFilters>
              )}
              <div className="text-end mb-2">
                {ShowTotal(pagination.total || 0)}
              </div>

              <Table
                loading={loading}
                columns={columns}
                dataSource={list}
                rowKey="_id"
                // pagination={{
                //   current: pagination.current,
                //   pageSize: pagination.pageSize,
                //   total: pagination.total,
                //   showSizeChanger: true,
                // }}
                pagination={{
                  current: pagination.current,
                  defaultPageSize: 10,
                  responsive: true,
                  total: pagination.total,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "30", "50"],
                }}
                className="ant-border-space"
              />
            </Card>
          </Col>
        </Row>
      </div>

      {visible && (
        <IntegrationForm
          show={visible}
          hide={() => setVisible(false)}
          data={selected}
          refresh={() => setRefresh((prev) => !prev)}
          isUpdate={isUpdate}
        />
      )}
    </>
  );
}

export default Index;
